import { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { formatDistanceToNow } from "date-fns";

import { TicketStatus } from "@signalist/shared-types";

import { PageHeader } from "@/components";
import { ErrorPage } from "@/components/ErrorPage/ErrorPage";
import { Loader } from "@/components/Loader/Loader";
import { ErrorPageType } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useConfirmTicketResolvedMutation,
	useGetTicketQuery,
} from "@/pages/Private/redux/tickets/tickets.api";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { lastTicketActivity } from "../../utils/ticketRowMeta";
import { TicketDetailHeader } from "./components/TicketDetailHeader";
import { TicketDetailRail } from "./components/TicketDetailRail";
import { TicketReplyComposer } from "./components/TicketReplyComposer";
import { TicketResolvedBanner } from "./components/TicketResolvedBanner";
import { TicketThread } from "./components/TicketThread";
import { ReplyOutcome } from "./hooks/useTicketReply";
import { ticketResolvedBy } from "./utils/ticketThread";

const isNotFound = (error: unknown): boolean =>
	typeof error === "object" && error !== null && (error as { status?: unknown }).status === 404;

/**
 * One support ticket (PREC-3938, spec §7): the thread, the reply box, the RESOLVED banner and the
 * rail. Reached through the list row and through PREC-3936's notification deep links. The route is
 * inside the SUPPORT_TICKETS FeatureRoute; the gate here is belt and braces so no request leaves
 * the browser while the flag is off (the API would answer 403).
 */
export const TicketDetail: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const ticketId = Number(id);
	const isFeatureActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const composerRef = useRef<HTMLTextAreaElement>(null);

	const {
		data: ticket,
		isLoading,
		error,
	} = useGetTicketQuery(ticketId, {
		skip: isFeatureActive !== true || Number.isNaN(ticketId),
	});
	const [confirmResolved, { isLoading: isConfirming }] = useConfirmTicketResolvedMutation();

	if (isFeatureActive === false) {
		return <ErrorPage type={ErrorPageType.Forbidden} />;
	}

	if (Number.isNaN(ticketId) || isNotFound(error)) {
		return <ErrorPage type={ErrorPageType.NotFound} />;
	}

	if (error) {
		return <ErrorPage type={ErrorPageType.ServerError} />;
	}

	if (isLoading || !ticket) {
		return <Loader />;
	}

	const activity = lastTicketActivity(ticket);
	const reportAttachmentError = (name: string) =>
		handleOpenSnackbar(t("support.detail.attachments.failed", { name }));

	const handleSent = (outcome: ReplyOutcome) => {
		if (!outcome.ok) {
			handleOpenSnackbar(t("support.detail.composer.failed"));

			return;
		}

		handleOpenSnackbar(
			outcome.failedAttachment
				? t("support.detail.composer.sentWithFailedUpload", { name: outcome.failedAttachment })
				: t("support.detail.composer.sent")
		);
	};

	const handleConfirm = async () => {
		try {
			await confirmResolved(ticket.id).unwrap();
			handleOpenSnackbar(t("support.detail.banner.closed"));
		} catch {
			handleOpenSnackbar(t("support.detail.banner.confirmFailed"));
		}
	};

	const focusComposer = () => {
		// `scrollIntoView` is missing in jsdom; the optional call keeps the spec honest.
		composerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
		composerRef.current?.focus();
	};

	return (
		<div className="flex h-full w-full flex-col">
			<PageHeader
				actions={
					<span className="text-xs text-text-meta">
						{t(`support.detail.hint.${activity.kind}`, {
							ago: formatDistanceToNow(new Date(activity.at), { addSuffix: true }),
						})}
					</span>
				}
				title={
					<span className="flex items-center gap-1.5">
						<Link className="text-text-meta hover:text-text" to={ROUTE_CONFIG.SUPPORT}>
							{t("support.detail.breadcrumb")}
						</Link>
						<span className="text-text-meta">›</span>
						<span>{t("support.detail.ticketNumber", { id: ticket.id })}</span>
					</span>
				}
			/>

			<div className="flex min-h-0 flex-1 gap-6">
				<section className="flex min-w-0 flex-1 flex-col gap-4">
					<TicketDetailHeader ticket={ticket} />
					<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
						{ticket.status === TicketStatus.RESOLVED && (
							<TicketResolvedBanner
								isConfirming={isConfirming}
								resolvedBy={ticketResolvedBy(ticket)}
								onConfirm={() => void handleConfirm()}
								onReply={focusComposer}
							/>
						)}
						<TicketThread ticket={ticket} onAttachmentError={reportAttachmentError} />
					</div>
					<TicketReplyComposer textareaRef={composerRef} ticketId={ticket.id} onSent={handleSent} />
				</section>
				<TicketDetailRail ticket={ticket} onAttachmentError={reportAttachmentError} />
			</div>
			{renderMessageSnackbar()}
		</div>
	);
};
